import useQueryPollUntilCondition from "@/libs/hooks/useQueryPollUntilCondition";
import { useEffect, useState } from "react";
import { TokenTransferStatus, TokenTransferStatusResponseBody } from "../types";
import { ERROR_CODES } from "@/libs/error/types";
import useInvalidateQuery from "@/src/hooks/useInvalidateQuery";
import { tokenTransferStatusForId } from "./getTokenTransferStatus";
import {
  TOKEN_TRANSFER_STATUS,
  USER_NFTS,
} from "@/libs/apis/onboarding/reactQueryCacheKeys";
import { useCurrentUser } from "@/libs/apis/onboarding/useUser";

const DEFAULT_POLLING_IN_MS = 2000;

const usePollTokenTransferStatus = (
  transactionId: string,
  isEnabled: boolean,
) => {
  const [enabled, setEnabled] = useState<boolean>(isEnabled);

  const { data, hasError, isFetching, status } =
    useQueryPollUntilCondition<TokenTransferStatusResponseBody>(
      async () => await tokenTransferStatusForId(transactionId),
      [TOKEN_TRANSFER_STATUS, transactionId],
      enabled,
      DEFAULT_POLLING_IN_MS,
      {
        refetchIntervalInBackground: true,
        retry: (failureCount, error) => {
          // if code is not a 404 we're not trying again
          const errorStatusCode = error.response?.status;
          const maxRetryTimes = 7;

          if (errorStatusCode !== ERROR_CODES.NOT_FOUND) {
            return false;
          }
          if (failureCount >= maxRetryTimes) {
            setEnabled(false);
            return false;
          }
          return true;
        },

        useErrorBoundary: ({ response }) => {
          return (
            response !== undefined &&
            (response.status === ERROR_CODES.INTERNAL_ERROR ||
              response.status === ERROR_CODES.FORBIDDEN ||
              response.status === ERROR_CODES.TOO_MANY_REQUESTS)
          );
        },
      },
    );

  useEffect(() => {
    if (data !== undefined) {
      if (
        data.status === TokenTransferStatus.Completed ||
        data.status === TokenTransferStatus.Failed ||
        hasError
      ) {
        setEnabled(false);
      }
    }
  }, [hasError, data]);

  return {
    data,
    hasError:
      // need mock error as FALSE in test until API done, NVS-4640, NVS-4609
      process.env.NEXT_PUBLIC_CURRENT_ENV !== "production" ? false : hasError,
    isFetching,
    setEnabled,
    queryEnabled: enabled,
    status,
  };
};

export default usePollTokenTransferStatus;
